import { inject } from '@angular/core';
import { ParamMap, ActivatedRoute, Params } from '@angular/router';
import { FilterMetadata, FilterMatchMode } from 'primeng/api';

import { patchQueryParamsFactory } from './qp-utils';

const equals = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const normalize = (value: unknown) => (Array.isArray(value) ? value : [value]);

const singleFilter = (source: FilterMetadata[] | FilterMetadata = []): unknown[] | undefined => {
  if (!Array.isArray(source)) {
    return normalize(source.value);
  }

  if (source.length > 1) {
    console.warn('Unsupported multi-filter', source);
    return undefined;
  }

  return normalize(source[0].value);
};

export type TableFilter<K extends string = string> = Record<K, FilterMetadata[]>;

export const qpToFiltersParam =
  <F extends Record<string, unknown>, K extends keyof F>(parser: (qp: ParamMap) => F, keys: K[]) =>
    (qp: ParamMap) => {
      const parsed = parser(qp);
      return {
        ...parsed,
        filters: Object.fromEntries(
          Object.entries(parsed)
            .filter((pair) => keys.includes(pair[0] as K))
            .map(([k, value]) => [
              k,
              [{ value, matchMode: Array.isArray(value) ? FilterMatchMode.IN : FilterMatchMode.IS }],
            ]),
        ) as TableFilter,
      };
    };

export const updateFiltersFactory = () => {
  const route = inject(ActivatedRoute);
  const patchQueryParams = patchQueryParamsFactory();

  return async (event: OnFilterEvent) => {
    const filters = event.filters ?? {};
    const patch = Object.entries(filters).map(([k, v]) => [k, singleFilter(v)] as const);

    if (patch.every(([k, v]) => equals(route.snapshot.queryParamMap.getAll(k), v))) {
      return true;
    }

    return patchQueryParams({ ...Object.fromEntries(patch), offset: undefined });
  };
};

export const dropFilters =
  <F extends object>(serializer = (parsed: F): Params => parsed) =>
    (parsed: F): Params => ({ ...serializer(parsed), filters: undefined });

export interface OnFilterEvent {
  filters?: {
    [s: string]: FilterMetadata[] | FilterMetadata | undefined;
  };
}
